import { Select, Space } from 'antd';
import Loading from 'commons/Loading';
import { find, isArray } from 'lodash';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  defaultSelectFormatter,
  getSelectLabelValue,
  hasLabelValue,
} from 'utils/helpers/select_helpers';
import { commonInput } from '../formsComponents/formTypes';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';


import classNames from 'classnames';
import CommonButton from 'commons/Buttons/CommonButton';

const { Option } = Select;

const NEW_ITEM = 'CREATE_NEW_ITEM';
interface SelectRemoteFieldProps extends Partial<commonInput> {
  showAll?: boolean; // se mostrare tutti i risultati senza ricerca remota
  remote?: boolean; // se i dati vengono presi da remoto
  dynamic?: boolean;
  minChar?: number;
  mode?: 'multiple' | 'tags';
  allowClear?: boolean;
  options?: any[];
  placeholder?: string;
  service?: any;
  notFound?: string;
  value?: any;
  labelInValue?: boolean;
  formatter?: Function;
  debug?: boolean;
  onSelected?: any;
  style?: any;
  selectFirst?: boolean;
  loadOnFirstClick?: boolean;
  /**Se voglio che al click venga sempre rifatta la chiamata per avere le options */
  forceFetchOnFocus?: boolean;
  hasFeedback?: boolean;
  /**Se permette inserimento di dati nuovi */
  creatable?: boolean;
  /**Se può essere bloccata / sbloccata */
  hasLock?: boolean;
}

const SelectRemoteField: React.FC<SelectRemoteFieldProps> = ({
  showAll = false, // se mostrare tutti i risultati senza ricerca remota
  remote = false, // se i dati vengono presi da remoto
  minChar = 1,
  placeholder = '',
  mode = null,
  allowClear = true,
  debug = false,
  options,
  service,
  notFound,
  value,
  formatter = defaultSelectFormatter,
  labelInValue = hasLabelValue(value),
  onSelected,
  loadOnFirstClick = false,
  selectFirst = false,
  hasFeedback = false,
  forceFetchOnFocus = false,
  creatable = false,
  hasLock = false,
  ...props
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [data, setData] = useState([]);

  const firstServiceFetch = useRef(false);

  const customValue = useRef<any>(undefined);
  const [inputValue, setInputValue] = useState<any>();

  const isDynamic = useMemo(() => {
    const _isDynamic = props.dynamic || props.conditions || false;
    return _isDynamic;
  }, [props.dynamic, props.conditions]);

  // useEffect(() => {

  //   if (forceFetchOnFocus) {
  //     setData([]);
  //   }
  // }, [service]);
  // @ts-ignore
  const OPTIONS: any[] = useMemo(() => {
    let _options = options || [];
    if (customValue.current) {
      _options = [..._options, customValue.current];
    }
    if (_options?.length) return formatter(_options);
    return formatter(data);
  }, [options, service, data, isFetching]);

  useEffect(() => {
    if (isDynamic || !loadOnFirstClick || value || forceFetchOnFocus) {
      firstGet();
    }
  }, [service, isDynamic]);

  const firstGet = () => {
    if (!firstServiceFetch?.current) {
      if (showAll || isDynamic) {
        getRemoteData(value || '');
      } else if (value && !labelInValue) {
        getRemoteData(getValue(value));
      }
    }
  };

  const onSearch = (currentValue) => {
    if (creatable) {
      setInputValue(currentValue);
    }

    if (currentValue.length >= minChar && !showAll) {
      getRemoteData(currentValue);
    } else if (loadOnFirstClick || showAll) {
      getRemoteData('');
    }
  };

  const getRemoteData = debounce(async (currentValue) => {
    if (showAll && OPTIONS.length && !isDynamic) return OPTIONS;

    if (service && !isFetching) {
      getFromService(currentValue);
    }
  }, 500);

  const getFromService = async (currentValue) => {
    setIsFetching(true);
    // @ts-ignore
    console.count(`TCL: getRemoteData -> ${props.name}` || props.label);
    firstServiceFetch.current = true;
    const _data = await service(currentValue);
    setData(_data);
    setIsFetching(false);

    setTimeout(() => {
      if (props.required || selectFirst) {
        if (_data.length === 1) {
          const firstValue = getSelectLabelValue(_data[0]);
          if (value !== firstValue.value) {
            //
            // @ts-ignore
            props.onChange && props.onChange(firstValue.value, firstValue);
          }
        }
      }
    }, 0);
    return _data;
  };

  const filterFunc = (input, option) => {
    const regex = new RegExp(input, 'gi');
    if (regex.test(option.children)) {
      return option;
    }
  };

  const onFocus = () => {
    firstGet();

    if (forceFetchOnFocus) {
      getFromService('');
    }
  };

  const onBlur = () => {
    if (forceFetchOnFocus) {
      setData([]);
    }
  };

  const getValue = (val) => {
    if (labelInValue) return val || null;
    if (!val) return null;
    let value = val;

    if (isArray(val)) {
      value =
        val?.[0]?.value ?? val?.[0]?.id ?? val?.[0]?.name ?? val?.[0]?.key ?? val?.[0] ?? null;
    } else {
      value = val?.value ?? val?.id ?? val?.name ?? val?.key ?? val ?? null;
    }

    return value?.label || value || null;
  };

  const theValue = useMemo(() => {
    let theVal = value;
    if (labelInValue) return theVal || null;

    if (mode === 'multiple' || mode === 'tags') {
      if (Array.isArray(value) && value?.length) {
        theVal = value.map((el) => getValue(el));
      } else {
        return [];
      }
    } else {
      theVal = getValue(value);
    }
    return theVal;
  }, [value]);

  const [isLocked, setIsLocked] = useState(undefined);
  const toggleLock = () => {
    setIsLocked((lock) => !lock);
  };

  useEffect(() => {
    if (hasLock && theValue && isLocked === undefined) {
      setIsLocked(true);
    }
  }, [theValue, isLocked]);

  return (
    <Loading spinning={isFetching}>
      <div className={classNames('flex-row', { hasLock })}>
        <Select
          {...props}
          value={theValue}
          allowClear={allowClear}
          mode={mode}
          showSearch={true}
          onClear={() => console.log('on clear')}
          loading={isFetching}
          // optionLabelProp="label"
          placeholder={`${placeholder || ''} `}
          onSearch={onSearch}
          onChange={(value = null, option) => {

            if (creatable && value === NEW_ITEM) {
              // MakeApiCall & get new id

              customValue.current = { label: inputValue, value: inputValue };
              setInputValue(undefined);
            }
            // @ts-ignore
            if (props.onChange) {
              let optionValue = null;
              if (data.length) {
                try {
                  optionValue = find(data, (opt) => opt.id === value);
                } catch (e) {
                  console.warn(e);
                }
              }
              //
              props.onChange(value || null, option, optionValue);
            }
            // props.onChange && props.onChange(value, option)
            onSelected && onSelected(value, option);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          labelInValue={labelInValue}
          // onSelect={value => onSelect(value)}
          notFoundContent={
            isFetching ? (
              <Loading tip="loading.." size="small">
                Cercando..
              </Loading>
            ) : (
              notFound || ''
            )
          }
          filterOption={filterFunc}
          disabled={props.disabled || isLocked}
        >
          {creatable && inputValue && OPTIONS.filter((o) => o === inputValue).length === 0 && (
            <Option key={inputValue} value={NEW_ITEM}>
              {'CREA ' + inputValue}
            </Option>
          )}

          {OPTIONS &&
            OPTIONS.length &&
            OPTIONS.map((option, index) => (
              <Option key={option.key} value={option.key} disabled={option.disabled}>
                {debug && <span>{option.key}-</span>}
                {option.label}
              </Option>
            ))}
        </Select>
        {hasLock && (
          <span className={classNames('lockInput', { isLocked })}>
            <CommonButton
              type="link"
              tooltip={isLocked ? 'Sblocca' : 'Blocca'}
              icon={isLocked ? <LockOutlined /> : <UnlockOutlined />}
              onClick={toggleLock}
            />
          </span>
        )}
      </div>
      {/* <JsonLog src={OPTIONS} visible /> */}
    </Loading>
  );
};

export default SelectRemoteField;
